import {
  events,
  virtualPagesTypes,
  virtualPagesNames,
  virtualPagesNestedLocations,
  productLists,
  addCartCTAType,
  addCartLocation,
  VIEW_ONLINE,
  rangeTabs,
  ONLINE_SOFTWARE,
  PDP_SOFTWARE,
  EXTERNAL_PARTNER_NAME,
  refinementStateChangedActionNames,
} from './constants';
import { getAddToCartLocation, getPageSubSubCategory, setActualNoParam } from './utils';
import {
  getLookupValueProductLists,
  getLookupValueVirtualPageName,
  getLookupValuePageTabs,
} from './lookups';
import type {
  EcommerceEventParams,
  SelectItemCTAEvent,
  ViewItemListEvent,
  RangeCtaClickEventParams,
  RangeCtaClickEvent,
  AddToCartEvent,
  AddToCartEventParams,
  AddToCartMainParams,
  AddToCartProductCardParams,
  BuyOnlineClickEvent,
  CardIDCTAClickEventParams,
  ClickExternalPartnerLinkEventParams,
  ClickExternalPartnerLinkEventType,
  CtaClickEventParams,
  CtaClickEvent,
  CtaEvent,
  ComparatorPageLoadEvent,
  ComparatorPageLoadParams,
  CsExitToSchneiderEvent,
  CsExitToSchneiderEventParams,
  DocumentDownloadEvent,
  DocumentDownloadParams,
  EeListClickEvent,
  ExitToSchneiderEventParams,
  ExitToSchneiderEventType,
  HMCEventParams,
  MainDocumentDropDownParams,
  MediaGalleryCtaClickEvent,
  MediaGalleryCtaClickEventParams,
  PageViewEvent,
  PaginationClickEventParams,
  ProductSelectorVirtualPageEventParams,
  ProductVariantsClickEventParams,
  RangeTabClickEventParams,
  RemoveFromCartEvent,
  RemoveFromCartParams,
  VideoCompleteEvent,
  VideoEventParams,
  VideoStartEvent,
  ViewExternalPartnerLinkEvent,
  ViewExternalPartnerLinkEventParams,
  ViewItemEvent,
  ViewItemEventParams,
  VirtualPageBuyOnlineCircularParam,
  VirtualPageRelatedProductsParam,
  PageViewPayload,
  RefinementStateChangedEventParams,
  RefinementStateChangedEvent,
} from './types/events';
import type { Download } from './types/common';
import { BuildPageViewPayloadParams } from './types/events/google-analytics/page_view';

export const productSelectorAddToCartEvent = (params: AddToCartEventParams): AddToCartEvent => {
  const { products } = params;
  const ctaType = products.length > 1
    ? addCartCTAType.BUTTON_GROUP_OF_PRODUCTS
    : addCartCTAType.BUTTON_SINGLE_PRODUCT;

  return {
    event: events.ADD_TO_CART,
    addToCartLocation: getAddToCartLocation(params, addCartLocation.PRODUCT_SELECTOR, ctaType),
    ...params,
  };
};

export const addToCartRelatedProductsEvent = (params: AddToCartEventParams): AddToCartEvent => {
  return {
    event: events.ADD_TO_CART,
    addToCartLocation: getAddToCartLocation(
      params,
      productLists.RELATED_PRODUCTS_MODAL,
      addCartCTAType.BUTTON_SINGLE_PRODUCT,
    ),
    ...params,
  };
};

export const addToCartMainEvent = (params: AddToCartMainParams): AddToCartEvent => {
  const { isSticky, ...rest } = params;
  const location = isSticky ? addCartLocation.STICKY_BAR : addCartLocation.PRODUCT_MAIN;
  return {
    event: events.ADD_TO_CART,
    addToCartLocation: getAddToCartLocation(params, location, addCartCTAType.BUTTON_SINGLE_PRODUCT),
    ...rest,
  };
};

export const addToCartProductCardEvent = (params: AddToCartProductCardParams): AddToCartEvent => {
  const { isGridView, ...rest } = params;
  const location = isGridView ? productLists.GRID_VIEW_PRODUCTS : productLists.LIST_VIEW_PRODUCTS;
  return {
    event: events.ADD_TO_CART,
    addToCartLocation: getAddToCartLocation(params, location, addCartCTAType.BUTTON_SINGLE_PRODUCT),
    ...rest,
  };
};

export const addToCartSubstitution = (params: AddToCartEventParams): AddToCartEvent => {
  const { products } = params;
  const ctaType = products.length > 1
    ? addCartCTAType.BUTTON_GROUP_OF_PRODUCTS
    : addCartCTAType.BUTTON_SINGLE_PRODUCT;
  return {
    event: events.ADD_TO_CART,
    addToCartLocation: getAddToCartLocation(params, productLists.SUBSTITUTE_PRODUCTS, ctaType),
    ...params,
  };
};

export const addToCartAlternative = (params: AddToCartEventParams): AddToCartEvent => {
  return {
    event: events.ADD_TO_CART,
    addToCartLocation: getAddToCartLocation(
      params,
      productLists.ALTERNATIVE_PRODUTS,
      addCartCTAType.BUTTON_SINGLE_PRODUCT,
    ),
    ...params,
  };
};

export const downloadDocumentEvent = (params: Download): DocumentDownloadEvent => {
  return {
    event: events.DOWNLOAD_DOCUMENT,
    ...params,
  };
};

export const documentDownloadEvent = (params: DocumentDownloadParams): DocumentDownloadEvent => {
  const { isViewOnline, documentName, ...rest } = params;
  return {
    event: events.DOWNLOAD,
    documentName: isViewOnline ? VIEW_ONLINE : documentName,
    ...rest,
  };
};

export const removeFromFavoritesEvent = (params: RemoveFromCartParams): RemoveFromCartEvent => {
  return {
    event: events.REMOVE_FROM_CART,
    ...params,
  };
};

export const comparatorPageLoadEvent = (
  params: ComparatorPageLoadParams,
): ComparatorPageLoadEvent => {
  return {
    event: events.COMPARATOR,
    ...params,
  };
};

export const productVariantsClickEvent = (
  params: ProductVariantsClickEventParams,
): EeListClickEvent => {
  const { currency, ...product } = params;
  const listValue = getLookupValueProductLists(productLists.VARIANTS);

  return {
    event: events.PRODUCT_VIEW,
    currencyCode: currency,
    list: listValue,
    products: [ product ],
  };
};

export const buyOnlineClickEvent = (params: CardIDCTAClickEventParams): BuyOnlineClickEvent => {
  return {
    event: events.VIRTUAL_PAGE_VIEW,
    fromWebView: false,
    pageSubSubCategory: getPageSubSubCategory(
      params,
      virtualPagesTypes.MODAL_WINDOW,
      virtualPagesNames.BUY_ONLINE,
    ),
    ...params,
  };
};

export const documentsDownloadClickEvent = (params: CardIDCTAClickEventParams): PageViewEvent => {
  return {
    event: events.VIRTUAL_PAGE_VIEW,
    fromWebView: false,
    pageSubSubCategory: getPageSubSubCategory(
      params,
      virtualPagesTypes.MODAL_WINDOW,
      virtualPagesNames.DOWNLOAD,
    ),
    ...params,
  };
};

export const paginationClickEvent = (params: PaginationClickEventParams): PageViewEvent => {
  const { pageNoParam, articleTitle } = params;
  let page = '';
  if (pageNoParam) {
    const currentUrl = window.decodeURI(window.location.href);
    page = setActualNoParam(currentUrl, pageNoParam);
  }
  return {
    event: events.VIRTUAL_PAGE_VIEW,
    fromWebView: false,
    pageSubSubCategory: getPageSubSubCategory(params, virtualPagesTypes.PAGINATION),
    page,
    articleTitle,
  };
};

export const onlineSoftwareModalClickEvent = (type: string): PageViewEvent => {
  const virtualPageName = type === ONLINE_SOFTWARE || type === PDP_SOFTWARE
    ? virtualPagesNames.SEE_SOFTWARE
    : virtualPagesNames.SEE_CONFIGURATOR;
  return {
    event: events.VIRTUAL_PAGE_VIEW,
    fromWebView: false,
    pageSubSubCategory: getPageSubSubCategory(
      null,
      virtualPagesTypes.MODAL_WINDOW,
      virtualPageName,
    ),
  };
};

export const mediaGalleryPopupEvent = (): PageViewEvent => {
  return {
    event: events.VIRTUAL_PAGE_VIEW,
    fromWebView: false,
    pageSubSubCategory: getPageSubSubCategory(
      null,
      virtualPagesTypes.MODAL_WINDOW,
      virtualPagesNames.MEDIA_GALLERY,
    ),
  };
};

export const hmcEvent = (params: HMCEventParams): PageViewEvent => {
  const { pageCategoryId } = params;
  return {
    event: events.VIRTUAL_PAGE_VIEW,
    fromWebView: false,
    pageSubSubCategory: getPageSubSubCategory(
      params,
      virtualPagesTypes.MODAL_WINDOW,
      virtualPagesNames.HELP_ME_CHOOSE,
    ),
    ...(pageCategoryId && { pageCategoryId }),
  };
};

export const productSelectorVirtualPageEvent = (
  params?: ProductSelectorVirtualPageEventParams,
): PageViewEvent => {
  const { location, pageRangeId } = params || {};
  const rest = pageRangeId ? { pageRangeId } : {};

  let articleTitle = '';
  if (location === virtualPagesNestedLocations.HELP_ME_CHOOSE) {
    articleTitle = getLookupValueVirtualPageName(location);
  }

  return {
    event: events.VIRTUAL_PAGE_VIEW,
    fromWebView: false,
    pageSubSubCategory: getPageSubSubCategory(
      params,
      virtualPagesTypes.MODAL_WINDOW,
      virtualPagesNames.PRODUCT_SELECTOR,
    ),
    articleTitle,
    ...rest,
  };
};

export const virtualPageRelatedProductsEvent = (
  params: VirtualPageRelatedProductsParam,
): PageViewEvent => {
  return {
    event: events.VIRTUAL_PAGE_VIEW,
    fromWebView: false,
    pageSubSubCategory: getPageSubSubCategory(
      params,
      virtualPagesTypes.MODAL_WINDOW,
      virtualPagesNames.RELATED_PRODUCTS,
    ),
  };
};

export const buyOnlineVirtualPageEvent = (): PageViewEvent => {
  return {
    event: events.VIRTUAL_PAGE_VIEW,
    fromWebView: false,
    pageSubSubCategory: getPageSubSubCategory(
      null,
      virtualPagesTypes.MODAL_WINDOW,
      virtualPagesNames.BUY_ONLINE,
    ),
  };
};

export const buyOnlineCircularVirtualPageEvent = (
  params: VirtualPageBuyOnlineCircularParam,
): PageViewEvent => {
  return {
    event: events.VIRTUAL_PAGE_VIEW,
    fromWebView: false,
    pageSubSubCategory: getPageSubSubCategory(
      params,
      virtualPagesTypes.MODAL_WINDOW,
      virtualPagesNames.BUY_ONLINE_CIRCULAR,
    ),
  };
};

export const filtersVirtualPageEvent = (): PageViewEvent => {
  return {
    event: events.VIRTUAL_PAGE_VIEW,
    fromWebView: false,
    pageSubSubCategory: getPageSubSubCategory(
      null,
      virtualPagesTypes.MODAL_WINDOW,
      virtualPagesNames.FILTERS,
    ),
  };
};

export const mainDocumentsDropDownEvent = (params: MainDocumentDropDownParams): PageViewEvent => {
  return {
    event: events.VIRTUAL_PAGE_VIEW,
    fromWebView: false,
    pageSubSubCategory: getPageSubSubCategory(
      params,
      virtualPagesTypes.MODAL_WINDOW,
      virtualPagesNames.MAIN_DOCUMENTS,
    ),
  };
};

export const rangeTabClickEvent = (params: RangeTabClickEventParams): PageViewEvent => {
  const { tabHash } = params;
  const tabsArticleTitle = rangeTabs[tabHash];
  const articleTitle = getLookupValuePageTabs(tabsArticleTitle);

  return {
    event: events.VIRTUAL_PAGE_VIEW,
    fromWebView: false,
    pageSubSubCategory: getPageSubSubCategory(params, virtualPagesTypes.TABS_ON_RANGE),
    articleTitle,
  };
};

export const videoStartEvent = (params: VideoEventParams): VideoStartEvent => {
  return {
    event: events.VIDEO_START,
    ...params,
  };
};
export const videoCompleteEvent = (params: VideoEventParams): VideoCompleteEvent => {
  return {
    event: events.VIDEO_COMPLETE,
    ...params,
  };
};

export const clickExternalPartnerLinkEvent = ({
  // eslint-disable-next-line @typescript-eslint/naming-convention
  external_partner_url,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  external_partner_name = EXTERNAL_PARTNER_NAME,
}: ClickExternalPartnerLinkEventParams): ClickExternalPartnerLinkEventType => {
  return {
    event: events.CLICK_EXTERNAL_PARTNER_LINK,
    external_partner_name,
    external_partner_url,
  };
};

export const exitToSchneiderEvent = ({
  // eslint-disable-next-line @typescript-eslint/naming-convention
  link_url,
}: ExitToSchneiderEventParams): ExitToSchneiderEventType => {
  return {
    event: events.EXIT_TO_SCHNEIDER,
    link_url,
  };
};

export const viewExternalPartnerLink = ({
  // eslint-disable-next-line @typescript-eslint/naming-convention
  external_partner_url,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  external_partner_name,
}: ViewExternalPartnerLinkEventParams): ViewExternalPartnerLinkEvent => {
  return {
    event: events.VIEW_EXTERNAL_PARTNER_LINK,
    external_partner_name,
    external_partner_url,
  };
};

export const viewItemEvent = (params: ViewItemEventParams): ViewItemEvent => {
  return {
    event: events.VIEW_ITEM,
    ...params,
  };
};

export const csExitToSchneiderEvent = ({
  // eslint-disable-next-line @typescript-eslint/naming-convention
  link_url,
}: CsExitToSchneiderEventParams): CsExitToSchneiderEvent => {
  return {
    event: events.CS_EXIT_TO_SCHNEIDER,
    link_url,
  };
};

export const selectItemCTAEvent = (params: EcommerceEventParams): SelectItemCTAEvent => {
  return {
    event: events.SELECT_ITEM_CTA,
    ...params,
  };
};

export const viewItemListEvent = (params: EcommerceEventParams): ViewItemListEvent => {
  return {
    event: events.VIEW_ITEM_LIST,
    ...params,
  };
};

const createClickEvent = <T>(params: T): CtaEvent & T => {
  return {
    event: events.CTA_CLICK,
    ...params,
  };
};

export const ctaClickEvent = (params: CtaClickEventParams): CtaClickEvent => {
  return createClickEvent<CtaClickEventParams>(params);
};

export const rangeCtaClickEvent = (params: RangeCtaClickEventParams): RangeCtaClickEvent => {
  return createClickEvent<RangeCtaClickEventParams>(params);
};

export const mediaGalleryClickEvent = (
  params: MediaGalleryCtaClickEventParams,
): MediaGalleryCtaClickEvent => {
  return createClickEvent<MediaGalleryCtaClickEventParams>(params);
};

export const refinementStateChangedEvent = (
  params: RefinementStateChangedEventParams,
): RefinementStateChangedEvent => {
  const { isChecked, ...rest } = params;
  return {
    event: events.FILTER_CLICK,
    filterAction: isChecked
      ? refinementStateChangedActionNames.FILTER_SELECTED
      : refinementStateChangedActionNames.FILTER_DESELECTED,
    ...rest,
  };
};

export function buildPageViewPayload(params: BuildPageViewPayloadParams): PageViewPayload {
  let { articleTitle } = params;
  if (params.pageTabs
    && params.pesPageTypes
    && params.pageRangeId
    && params.pageTopCategory === params.pesPageTypes.productRangePage
    && params.tabKey
  ) {
    articleTitle = params.pageTabs[params.tabKey];
  }

  return {
    event: events.PAGE_VIEW,
    articleTitle: articleTitle || articleTitle === 0 ? articleTitle : null,
    pageTitle: params.pageTitle || null,
    fromWebView: params.fromWebView ?? null,
    pageLanguage: params.pageLanguage || null,
    businessUnit: params.businessUnit || null,
    template: params.template || null,
    pageTopCategory: params.pageTopCategory || null,
    pageCategory: params.pageCategory || null,
    pageSubCategory: params.pageSubCategory || null,
    pageSubSubCategory: params.pageSubSubCategory || null,
    pageCategoryId: params.pageCategoryId || null,
    pageSubCategoryId: params.pageSubCategoryId || null,
    pageRangeId: params.pageRangeId || null,
    pageProductId: params.pageProductId || null,
  };
}
